import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  CarriedUpSchema,
  carryUpConditionsByCarriedUp,
  type AdministeredOrganisation,
  type CarriedUp,
  type OrganisationStructure,
  type PersonInvolvementKind,
} from "@thalamos/haven-common";

import { carriedInto, summariseStructure } from "./describeStructure.js";
import { layOutStructure } from "./layOutStructure.js";
import { StructureDiagram } from "./StructureDiagram.js";

/** The structure once read, or whether reading it failed; `undefined` data with no failure is a read still in flight. */
export type LoadedStructure = {
  data: OrganisationStructure | undefined;
  failed: boolean;
};

/**
 * Where an organisation sits, and which permissions travel up each of the
 * memberships around it. Everything it says about permissions is read from
 * `carryUpConditionsByCarriedUp`, so a row added there needs no change here
 * beyond its translations.
 */
export const OrganisationStructureTab = ({
  organisation,
  structure,
  addRelationship,
}: {
  organisation: AdministeredOrganisation;
  structure: LoadedStructure;
  /** The form relating this organisation to another, where it may be offered. */
  addRelationship: ReactNode;
}) => {
  const { t, i18n } = useTranslation();
  const [carried, setCarried] = useState<CarriedUp>("view");
  const [allLevels, setAllLevels] = useState(true);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const expand = useCallback((groupOf: string) => {
    setExpanded((current) => new Set([...current, groupOf]));
  }, []);

  // Until this organisation's structure is read, `structure` may still hold
  // the last one's; never draw that as this one's.
  const data =
    structure.data?.organisationId === organisation.id
      ? structure.data
      : undefined;
  const layout = useMemo(
    () =>
      data === undefined
        ? undefined
        : layOutStructure(data, { allLevels, expanded }),
    [data, allLevels, expanded],
  );
  const carries = useMemo(
    () => (data === undefined ? undefined : carriedInto(data, carried)),
    [data, carried],
  );
  const summaries = useMemo(
    () =>
      data === undefined || layout === undefined || carries === undefined
        ? []
        : summariseStructure(data, layout, carries),
    [data, layout, carries],
  );

  // Joins alternatives ("A or B") in the words and order of the language shown.
  const alternatives = useMemo(
    () =>
      new Intl.ListFormat(i18n.resolvedLanguage ?? i18n.language, {
        type: "disjunction",
      }),
    [i18n.resolvedLanguage, i18n.language],
  );
  const listOf = (types: readonly PersonInvolvementKind[]) =>
    alternatives.format(types.map((kind) => t(`access.kind.${kind}`)));
  const settingName = (gate: string) =>
    t(`admin.configuration.settings.keys.${gate}`);
  const rule = carryUpConditionsByCarriedUp[carried];
  const permission = t(`admin.organisation.structure.carried.${carried}`);
  const involvements = listOf(rule.involvements);

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {t("admin.organisation.structure.description", {
          name: organisation.name,
        })}
      </Typography>

      {structure.failed ? (
        <Alert severity="error">
          {t("admin.organisation.structure.loadFailed")}
        </Alert>
      ) : data === undefined ||
        layout === undefined ||
        carries === undefined ? (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      ) : (
        <>
          <Alert severity="info" icon={false}>
            {rule.gatedBy === null
              ? t("admin.organisation.structure.rule", {
                  permission,
                  involvements,
                })
              : t("admin.organisation.structure.ruleGated", {
                  permission,
                  involvements,
                  setting: settingName(rule.gatedBy),
                })}{" "}
            {t(`admin.organisation.structure.reaches.${carried}`)}
          </Alert>

          {data.truncated ? (
            <Alert severity="warning">
              {t("admin.organisation.structure.truncated")}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
              <StructureDiagram
                layout={layout}
                carries={carries}
                involvements={involvements}
                gate={rule.gatedBy}
                fitKey={`${data.organisationId}:${String(allLevels)}`}
                label={t("admin.organisation.structure.diagram", {
                  name: organisation.name,
                })}
                onExpand={expand}
              />

              <Stack spacing={1}>
                <Typography variant="subtitle2" component="h2">
                  {t("admin.organisation.structure.memberships.heading")}
                </Typography>
                <Summaries
                  summaries={summaries}
                  permission={permission}
                  involvements={involvements}
                  setting={
                    rule.gatedBy === null ? "" : settingName(rule.gatedBy)
                  }
                />
              </Stack>
            </Stack>

            <Stack spacing={2} sx={{ width: { md: 300 }, flexShrink: 0 }}>
              <Typography
                variant="subtitle1"
                component="h2"
                id="carried-picker"
              >
                {t("admin.organisation.structure.picker")}
              </Typography>
              <ToggleButtonGroup
                orientation="vertical"
                exclusive
                color="primary"
                value={carried}
                aria-labelledby="carried-picker"
                onChange={(_event, value: unknown) => {
                  const picked = CarriedUpSchema.safeParse(value);
                  // A press on the picked button deselects it, which leaves
                  // nothing to show; keep the current one instead.
                  if (picked.success) {
                    setCarried(picked.data);
                  }
                }}
              >
                {CarriedUpSchema.options.map((option) => (
                  <ToggleButton
                    key={option}
                    value={option}
                    sx={{ justifyContent: "flex-start" }}
                  >
                    {t(`admin.organisation.structure.carried.${option}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              <Typography variant="subtitle2" component="h2" id="levels-shown">
                {t("admin.organisation.structure.levels.label")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                color="primary"
                value={allLevels ? "all" : "one"}
                aria-labelledby="levels-shown"
                onChange={(_event, value: unknown) => {
                  if (value === "all" || value === "one") {
                    setAllLevels(value === "all");
                  }
                }}
              >
                <ToggleButton value="one">
                  {t("admin.organisation.structure.levels.one")}
                </ToggleButton>
                <ToggleButton value="all">
                  {t("admin.organisation.structure.levels.all")}
                </ToggleButton>
              </ToggleButtonGroup>

              <Key />
            </Stack>
          </Stack>

          <ConfigurationInUse listOf={listOf} settingName={settingName} />
        </>
      )}

      {addRelationship}
    </Stack>
  );
};

const Summaries = ({
  summaries,
  permission,
  involvements,
  setting,
}: {
  summaries: ReturnType<typeof summariseStructure>;
  permission: string;
  involvements: string;
  setting: string;
}) => {
  const { t } = useTranslation();

  if (summaries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("admin.organisation.structure.memberships.none")}
      </Typography>
    );
  }

  return (
    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
      {summaries.map((summary) => (
        <Typography
          component="li"
          variant="body2"
          key={
            summary.kind === "membership"
              ? summary.receiverId
              : `${summary.operatorName}:${summary.operatedName}`
          }
        >
          {summary.kind === "operates"
            ? t("admin.organisation.structure.memberships.operates", {
                operator: summary.operatorName,
                operated: summary.operatedName,
              })
            : summary.carries
              ? t("admin.organisation.structure.memberships.carried", {
                  receiver: summary.receiverName,
                  count: summary.memberCount,
                  permission,
                  involvements,
                })
              : t("admin.organisation.structure.memberships.notCarried", {
                  receiver: summary.receiverName,
                  count: summary.memberCount,
                  permission,
                  setting,
                })}
        </Typography>
      ))}
    </Stack>
  );
};

const Key = () => {
  const { t } = useTranslation();
  const entries = [
    ["carriedTerm", "carried"],
    ["notCarriedTerm", "notCarried"],
    ["operatesTerm", "operates"],
  ] as const;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="overline" component="h2">
        {t("admin.organisation.structure.key.heading")}
      </Typography>
      <Stack component="dl" spacing={1} sx={{ m: 0 }}>
        {entries.map(([term, text]) => (
          <div key={term}>
            <Typography variant="body2" component="dt" sx={{ fontWeight: 700 }}>
              {t(`admin.organisation.structure.key.${term}`)}
            </Typography>
            <Typography variant="body2" component="dd" sx={{ m: 0 }}>
              {t(`admin.organisation.structure.key.${text}`)}
            </Typography>
          </div>
        ))}
      </Stack>
    </Paper>
  );
};

const ConfigurationInUse = ({
  listOf,
  settingName,
}: {
  listOf: (types: readonly PersonInvolvementKind[]) => string;
  settingName: (gate: string) => string;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" component="h2">
        {t("admin.organisation.structure.configuration.heading")}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("admin.organisation.structure.configuration.permission")}
              </TableCell>
              <TableCell>
                {t("admin.organisation.structure.configuration.from")}
              </TableCell>
              <TableCell>
                {t("admin.organisation.structure.configuration.gatedBy")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {CarriedUpSchema.options.map((option) => {
              const { involvements, gatedBy } =
                carryUpConditionsByCarriedUp[option];
              return (
                <TableRow key={option}>
                  <TableCell>
                    {t(`admin.organisation.structure.carried.${option}`)}
                  </TableCell>
                  <TableCell>{listOf(involvements)}</TableCell>
                  <TableCell>
                    {gatedBy === null
                      ? t("admin.organisation.structure.configuration.ungated")
                      : settingName(gatedBy)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
